import { io as socketIo } from "socket.io-client";
import {
  ErrorCodeSchema,
  SOCKET_NAMESPACE,
  SOCKET_TRANSPORT_PATH,
  createClassJoinAckSchema,
  createScopedCatalogChangedSchema,
} from "@canvaschool/contracts";

import { ApiError } from "../api/client";

import type { ErrorCode } from "@canvaschool/contracts";

/** The part of a Socket.IO client socket this transport uses. */
export type SocketLike = {
  connected: boolean;
  /** False once the server refused the connection in its middleware. */
  active: boolean;
  connect(): unknown;
  disconnect(): unknown;
  on(event: string, listener: (...args: any[]) => void): unknown;
  removeAllListeners(): unknown;
  timeout(milliseconds: number): {
    emit(
      event: string,
      payload: unknown,
      callback: (error: Error | null, ack?: unknown) => void,
    ): unknown;
  };
  volatile: { emit(event: string, payload: unknown): unknown };
};

export type CatalogTransportEvents = {
  onJoined(classId: string): void;
  onChanged(classId: string): void;
  onJoinFailed(code: ErrorCode): void;
  onDisconnected(): void;
  onAccessLost(code: ErrorCode): void;
};

export type CatalogTransportOptions = {
  classId: string;
  getCsrfToken(): Promise<string | null>;
  events: CatalogTransportEvents;
  io?: (uri: string, options: Record<string, unknown>) => SocketLike;
  ackTimeoutMilliseconds?: number;
  retryDelayMilliseconds?: (attempt: number) => number;
  setTimer?: (callback: () => void, milliseconds: number) => () => void;
};

export type CatalogTransport = {
  connect(): void;
  reconnect(): void;
  close(): void;
};

export type CatalogTransportFactory = (
  options: CatalogTransportOptions,
) => CatalogTransport;

const AUTHENTICATION = new Set<ErrorCode>(["UNAUTHENTICATED", "CSRF_INVALID"]);
const TRANSIENT = new Set<ErrorCode>(["RATE_LIMITED", "PERSISTENCE_FAILED"]);
const TRANSIENT_FETCH = new Set<string>([
  ...TRANSIENT,
  "NETWORK_FAILED",
  "INVALID_RESPONSE",
]);
const MAX_AUTHENTICATION_RETRIES = 3;

const defaultTimer = (callback: () => void, milliseconds: number) => {
  const handle = window.setTimeout(callback, milliseconds);
  return () => window.clearTimeout(handle);
};

const defaultRetryDelay = (attempt: number) =>
  Math.min(30_000, 1_000 * 2 ** attempt);

const defaultIo = socketIo as unknown as NonNullable<
  CatalogTransportOptions["io"]
>;

/** Same-origin Socket.IO subscription for one class canvas catalog. */
export const createCatalogTransport: CatalogTransportFactory = (options) => {
  const { classId, events } = options;
  const setTimer = options.setTimer ?? defaultTimer;
  const retryDelay = options.retryDelayMilliseconds ?? defaultRetryDelay;
  const ackTimeout = options.ackTimeoutMilliseconds ?? 15_000;
  const joinAckSchema = createClassJoinAckSchema(classId);
  const catalogChangedSchema = createScopedCatalogChangedSchema(classId);

  let socket: SocketLike | null = null;
  let closed = false;
  let sessionGone = false;
  let tokenUnavailable = false;
  let revokedByServer = false;
  let connectAttempts = 0;
  let joinAttempts = 0;
  const timers = new Set<() => void>();

  const reportJoinFailed = (code: ErrorCode) => {
    revokedByServer = true;
    events.onJoinFailed(code);
  };

  const reportAccessLost = (code: ErrorCode) => {
    revokedByServer = true;
    events.onAccessLost(code);
  };

  const cancelTimers = () => {
    for (const cancel of timers) {
      cancel();
    }
    timers.clear();
  };

  const later = (callback: () => void, attempt: number) => {
    let fired = false;
    let cancel: () => void = () => undefined;
    cancel = setTimer(() => {
      fired = true;
      timers.delete(cancel);
      if (!closed) {
        callback();
      }
    }, retryDelay(attempt));
    if (!fired) {
      timers.add(cancel);
    }
  };

  const join = () => {
    const current = socket;
    if (!current || closed || !current.connected) {
      return;
    }
    current
      .timeout(ackTimeout)
      .emit("class:join", { classId }, (error, ack) => {
        if (closed || socket !== current) {
          return;
        }
        if (error && !current.connected) {
          return;
        }
        const parsed = error ? null : joinAckSchema.safeParse(ack);
        if (parsed?.success && parsed.data.ok) {
          joinAttempts = 0;
          events.onJoined(parsed.data.classId);
          return;
        }
        const code =
          parsed?.success && !parsed.data.ok ? parsed.data.code : null;
        if (code && AUTHENTICATION.has(code)) {
          reconnect();
        } else if (code === null || TRANSIENT.has(code)) {
          later(join, joinAttempts++);
        } else {
          reportJoinFailed(code);
        }
      });
  };

  const attach = (current: SocketLike) => {
    current.on("connect", () => {
      cancelTimers();
      connectAttempts = 0;
      revokedByServer = false;
      join();
    });
    current.on(
      "connect_error",
      (error: Error & { data?: { code?: unknown } }) => {
        if (closed || current.active || sessionGone) {
          return;
        }
        if (tokenUnavailable) {
          later(() => {
            if (socket === current) {
              current.connect();
            }
          }, connectAttempts++);
          return;
        }
        const parsed = ErrorCodeSchema.safeParse(
          error.data?.code ?? error.message,
        );
        const code: ErrorCode = parsed.success
          ? parsed.data
          : "PERSISTENCE_FAILED";
        const retry =
          TRANSIENT.has(code) ||
          (AUTHENTICATION.has(code) &&
            connectAttempts < MAX_AUTHENTICATION_RETRIES);
        if (retry) {
          later(() => {
            if (socket === current) {
              current.connect();
            }
          }, connectAttempts++);
        } else {
          reportAccessLost(code);
        }
      },
    );
    current.on("disconnect", (reason: string) => {
      events.onDisconnected();
      if (reason === "io server disconnect" && !revokedByServer && !closed) {
        options.getCsrfToken().then(
          (token) => {
            if (closed || socket !== current || revokedByServer) {
              return;
            }
            tokenUnavailable = false;
            if (token === null) {
              sessionGone = true;
              reportAccessLost("UNAUTHENTICATED");
              return;
            }
            later(() => {
              if (socket === current) {
                current.connect();
              }
            }, connectAttempts++);
          },
          (error: unknown) => {
            if (closed || socket !== current || revokedByServer) {
              return;
            }
            const code =
              error instanceof ApiError ? error.code : "NETWORK_FAILED";
            if (
              TRANSIENT_FETCH.has(code) ||
              (error instanceof ApiError && error.status >= 500)
            ) {
              tokenUnavailable = true;
              later(() => {
                if (socket === current) {
                  current.connect();
                }
              }, connectAttempts++);
            } else {
              sessionGone = true;
              reportAccessLost("UNAUTHENTICATED");
            }
          },
        );
      }
    });
    current.on("catalog:changed", (payload: unknown) => {
      const parsed = catalogChangedSchema.safeParse(payload);
      if (parsed.success) {
        events.onChanged(parsed.data.classId);
      }
    });
  };

  const connect = () => {
    if (closed) {
      return;
    }
    if (!socket) {
      socket = (options.io ?? defaultIo)(SOCKET_NAMESPACE, {
        path: SOCKET_TRANSPORT_PATH,
        withCredentials: true,
        autoConnect: false,
        transports: ["websocket"],
        auth: (callback: (data: { csrfToken: string }) => void) => {
          options.getCsrfToken().then(
            (token) => {
              tokenUnavailable = false;
              sessionGone = token === null;
              if (token === null) {
                reportAccessLost("UNAUTHENTICATED");
              }
              callback({ csrfToken: token ?? "" });
            },
            (error: unknown) => {
              const code =
                error instanceof ApiError ? error.code : "NETWORK_FAILED";
              if (
                TRANSIENT_FETCH.has(code) ||
                (error instanceof ApiError && error.status >= 500)
              ) {
                tokenUnavailable = true;
              } else {
                sessionGone = true;
                reportAccessLost(code as ErrorCode);
              }
              callback({ csrfToken: "" });
            },
          );
        },
      });
      attach(socket);
    }
    socket.connect();
  };

  const reconnect = () => {
    if (closed) {
      return;
    }
    sessionGone = false;
    revokedByServer = false;
    connectAttempts = 0;
    if (!socket) {
      connect();
      return;
    }
    socket.disconnect();
    socket.connect();
  };

  return {
    connect,
    reconnect,
    close() {
      closed = true;
      cancelTimers();
      socket?.removeAllListeners();
      socket?.disconnect();
    },
  };
};
